import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import FormShell from '../../components/FormShell';
import ErrorBanner from '../../components/ErrorBanner';
import RichTextEditor from '../../components/RichTextEditor';
import PhotoUploader from '../../components/PhotoUploader';
import CountryCombobox from '../../components/CountryCombobox';
import Combobox, { type ComboboxOption } from '../../components/Combobox';
import { TIMEZONES, isKnownTimeZone, timezoneLabel } from '../../lib/timezones';
import { supabase } from '../../lib/supabase';
import { sanitizeRichText } from '@viet-parenting/supabase/sanitize';
import { slugifyWithTimestamp } from '@viet-parenting/supabase/slug';
import type {
  Event as EventRow,
  AgeGroup,
  Country,
  EventType,
  Status,
} from '@viet-parenting/supabase/types';
import { EventDetail } from '@viet-parenting/content/detail';
import PreviewModal from '../../components/PreviewModal';
import PreviewButton from '../../components/PreviewButton';
import { draftIdentity } from '../../lib/preview';

const AGES: ReadonlyArray<{ value: AgeGroup; label: string }> = [
  { value: 'kindergarten', label: 'Mầm non (3–5t)' },
  { value: 'primary', label: 'Tiểu học (6–11t)' },
  { value: 'middle', label: 'THCS (12–15t)' },
  { value: 'above_15', label: 'Trên 15 tuổi' },
];

const TYPES: ReadonlyArray<{ value: EventType; label: string; hint: string }> = [
  { value: 'offline', label: '📍 Trực tiếp (Offline)', hint: 'Có địa điểm cụ thể' },
  { value: 'online', label: '🌐 Trực tuyến (Online)', hint: 'Hiển thị ở mọi quốc gia' },
];

interface FormState {
  name_vi: string;
  name_en: string;
  description_vi: string;
  description_en: string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  event_type: EventType;
  venue_country: string;
  venue_city: string;
  venue_address: string;
  age_groups: AgeGroup[];
  photos: string[];
  status: Status;
  featured: boolean;
}

const EMPTY: FormState = {
  name_vi: '',
  name_en: '',
  description_vi: '',
  description_en: '',
  starts_at: '',
  ends_at: '',
  timezone: 'Europe/Berlin',
  event_type: 'offline',
  venue_country: '',
  venue_city: '',
  venue_address: '',
  age_groups: [],
  photos: [],
  status: 'pending',
  featured: false,
};

/**
 * The database shape of the form, shared by the save mutation and the preview
 * so an admin can never see something a save would not write.
 */
function toPayload(state: FormState) {
  const isOnline = state.event_type === 'online';
  const location = isOnline
    ? { venue_country: 'global', venue_city: '', venue_address: null }
    : {
        venue_country: state.venue_country,
        venue_city: state.venue_city.trim(),
        venue_address: state.venue_address.trim(),
      };

  return {
    name_vi: state.name_vi.trim(),
    name_en: state.name_en.trim() || null,
    description_vi: sanitizeRichText(state.description_vi) || null,
    description_en: state.description_en ? sanitizeRichText(state.description_en) : null,
    starts_at: new Date(state.starts_at).toISOString(),
    ends_at: new Date(state.ends_at).toISOString(),
    timezone: state.timezone,
    event_type: state.event_type,
    ...location,
    age_groups: state.age_groups,
    photos: state.photos,
    status: state.status,
    featured: state.featured,
  };
}

function toPreviewRow(state: FormState, id: string | undefined): EventRow {
  return {
    ...draftIdentity(state.name_vi),
    ...(id ? { id } : {}),
    ...toPayload(state),
  } as unknown as EventRow;
}

interface Props {
  mode: 'new' | 'edit';
}

export default function AdminEventForm({ mode }: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<FormState>(EMPTY);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const timezoneOptions = useMemo<ComboboxOption[]>(
    () => TIMEZONES.map((tz) => ({ value: tz, label: timezoneLabel(tz) })),
    [],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('countries').select('*').order('sort');
      if (!active) return;
      setCountries((data ?? []) as Country[]);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mode === 'new' || !id) return;
    let active = true;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      if (dbError || !data) {
        setError(dbError?.message ?? 'Không tìm thấy sự kiện.');
        setLoading(false);
        return;
      }
      const ev = data as EventRow;
      setState({
        name_vi: ev.name_vi,
        name_en: ev.name_en ?? '',
        description_vi: ev.description_vi ?? '',
        description_en: ev.description_en ?? '',
        starts_at: toLocalInput(ev.starts_at),
        ends_at: toLocalInput(ev.ends_at),
        timezone: ev.timezone,
        event_type: ev.event_type ?? (ev.venue_city ? 'offline' : 'online'),
        // `global` is how the DB stores "no country" — the picker shows it as empty.
        venue_country: ev.venue_country === 'global' ? '' : ev.venue_country,
        venue_city: ev.venue_city,
        venue_address: ev.venue_address ?? '',
        age_groups: ev.age_groups,
        photos: ev.photos,
        status: ev.status,
        featured: ev.featured ?? false,
      });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [mode, id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const toggleAge = (a: AgeGroup) =>
    update(
      'age_groups',
      state.age_groups.includes(a) ? state.age_groups.filter((x) => x !== a) : [...state.age_groups, a],
    );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.name_vi.trim() || !state.starts_at || !state.ends_at) {
      setError('Tên sự kiện và thời gian là bắt buộc.');
      return;
    }
    if (new Date(state.starts_at) >= new Date(state.ends_at)) {
      setError('Thời gian kết thúc phải sau thời gian bắt đầu.');
      return;
    }
    if (!isKnownTimeZone(state.timezone)) {
      setError(`Múi giờ «${state.timezone}» không hợp lệ. Chọn một múi giờ từ danh sách.`);
      return;
    }
    if (
      state.event_type !== 'online' &&
      (!state.venue_country || !state.venue_city.trim() || !state.venue_address.trim())
    ) {
      setError('Sự kiện trực tiếp cần đủ Quốc gia, Thành phố và Địa chỉ.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = toPayload(state);

    const result =
      mode === 'new'
        ? await supabase
            .from('events')
            .insert({ ...payload, slug: slugifyWithTimestamp(state.name_vi) })
        : await supabase.from('events').update(payload).eq('id', id!);

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    navigate('/admin/events');
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !id) return;
    const ok = window.confirm(
      `Xóa sự kiện "${state.name_vi}"? Hành động này không thể hoàn tác.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    const { error: dbError } = await supabase.from('events').delete().eq('id', id);
    setDeleting(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    navigate('/admin/events');
  };

  const title = mode === 'new' ? 'Tạo sự kiện mới' : 'Chỉnh sửa sự kiện';

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <AdminTabs active="events" />
          <FormShell title={title} maxWidth="max-w-6xl">
            <p className="text-sm text-gray-400">Đang tải…</p>
          </FormShell>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="events" />
        <FormShell title={title} maxWidth="max-w-6xl">
          <ErrorBanner message={error} />

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-8">
            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Ảnh sự kiện</h2>
              <PhotoUploader value={state.photos} onChange={(ids) => update('photos', ids)} maxFiles={6} />
            </section>

            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Thông tin</h2>
              <Field label="Tên (tiếng Việt)" required>
                <input
                  type="text"
                  required
                  value={state.name_vi}
                  onChange={(e) => update('name_vi', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Tên (tiếng Anh)">
                <input
                  type="text"
                  value={state.name_en}
                  onChange={(e) => update('name_en', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Mô tả (tiếng Việt)">
                <RichTextEditor
                  value={state.description_vi}
                  onChange={(html) => update('description_vi', html)}
                  placeholder="Mô tả sự kiện..."
                />
              </Field>
              <Field label="Mô tả (tiếng Anh)">
                <RichTextEditor
                  value={state.description_en}
                  onChange={(html) => update('description_en', html)}
                  placeholder="Event description..."
                />
              </Field>
            </section>
              </div>

              <div className="space-y-8">
            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Thời gian & địa điểm</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Bắt đầu" required>
                  <input
                    type="datetime-local"
                    required
                    value={state.starts_at}
                    onChange={(e) => update('starts_at', e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Kết thúc" required>
                  <input
                    type="datetime-local"
                    required
                    value={state.ends_at}
                    onChange={(e) => update('ends_at', e.target.value)}
                    className={inputClass}
                  />
                </Field>
              </div>
              <Field label="Múi giờ" required>
                <Combobox
                  value={state.timezone}
                  onChange={(tz) => update('timezone', tz)}
                  options={timezoneOptions}
                  placeholder="Gõ để tìm múi giờ… (vd: Brussels)"
                  emptyLabel="Không tìm thấy múi giờ."
                  className={inputClass}
                />
                {state.timezone && !isKnownTimeZone(state.timezone) && (
                  <p className="mt-1 text-xs text-rose-600">
                    «{state.timezone}» không phải múi giờ hợp lệ — hãy chọn lại từ danh sách.
                  </p>
                )}
              </Field>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hình thức <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {TYPES.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex flex-col gap-0.5 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                        state.event_type === opt.value
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 hover:border-red-200'
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="radio"
                          name="event_type"
                          checked={state.event_type === opt.value}
                          onChange={() => update('event_type', opt.value)}
                        />
                        {opt.label}
                      </span>
                      <span className="text-xs text-gray-500 pl-6">{opt.hint}</span>
                    </label>
                  ))}
                </div>
              </div>

              {state.event_type === 'offline' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Quốc gia" required>
                      <CountryCombobox
                        value={state.venue_country}
                        onChange={(code) => update('venue_country', code)}
                        countries={countries}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Thành phố" required>
                      <input
                        type="text"
                        required
                        value={state.venue_city}
                        onChange={(e) => update('venue_city', e.target.value)}
                        placeholder="Berlin"
                        className={inputClass}
                      />
                    </Field>
                  </div>
                  <Field label="Địa chỉ chi tiết" required>
                    <input
                      type="text"
                      required
                      value={state.venue_address}
                      onChange={(e) => update('venue_address', e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                </>
              ) : (
                <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  Sự kiện trực tuyến không cần địa điểm và sẽ hiển thị ở trang của mọi quốc gia.
                </p>
              )}
            </section>

            <section className="brand-card p-6 space-y-5">
              <h2 className="font-semibold">Đối tượng</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Độ tuổi</label>
                <div className="flex flex-wrap gap-4">
                  {AGES.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.age_groups.includes(opt.value)}
                        onChange={() => toggleAge(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </section>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-8 px-4 sm:px-6 py-4 bg-white/75 backdrop-blur-md border-t border-gray-200 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/admin/events')}
                className="border border-gray-200 text-gray-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:border-red-300 transition-colors"
              >
                Hủy
              </button>
              <PreviewButton
                onClick={() => setPreviewing(true)}
                disabled={!state.name_vi.trim() || !state.starts_at || !state.ends_at}
              />
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="border border-red-200 text-red-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Đang xóa…' : 'Xóa'}
                </button>
              )}
              <div className="flex items-center gap-4 ml-auto text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.featured}
                    onChange={(e) => update('featured', e.target.checked)}
                  />
                  <span className="font-medium text-gray-700">★ Featured</span>
                </label>
                <span className="font-medium text-gray-700">Trạng thái:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="pending"
                    checked={state.status === 'pending'}
                    onChange={() => update('status', 'pending')}
                  />
                  Pending
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="verified"
                    checked={state.status === 'verified'}
                    onChange={() => update('status', 'verified')}
                  />
                  Active
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu…' : mode === 'new' ? 'Tạo sự kiện' : 'Lưu thay đổi'}
              </button>
            </div>
          </form>
        </FormShell>
      </div>

      {previewing && (
        <PreviewModal onClose={() => setPreviewing(false)}>
          {(ctx) => <EventDetail event={toPreviewRow(state, id)} ctx={ctx} />}
        </PreviewModal>
      )}
    </AppShell>
  );
}

const inputClass =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}
function Field({ label, required, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}
